import { useMemo, useState } from 'react'
import { usePagination, useSecondOrderCallback } from '@chilecompra/react-kit'
import { useInput } from '@chilecompra/react-kit/components'
import { ascendingSort, descendingSort, filtering, SORT_DIRECTIONS_DICTIONARY } from './DataTable.utils'

/**
 * The DataTable custom hook.
 */
const useDataTable = ({ initialHeaders, initialHeadersCellsProps, initialPage, initialRecords, initialRows }) => {
  const initialDirectionValue = useMemo(() => {
    const initialDirectionIndex =
      initialHeaders.findIndex(
        (_, index) =>
          !!initialHeadersCellsProps[index]?.enableSort && !!initialHeadersCellsProps[index]?.initialDirection
      ) || {}

    if (initialDirectionIndex !== -1) {
      return initialHeadersCellsProps[initialDirectionIndex]?.initialDirection
    }

    return SORT_DIRECTIONS_DICTIONARY.DESC
  }, [initialHeaders, initialHeadersCellsProps])
  const [sortColumnValue, setSortColumn] = useState(() => {
    const { value: initialColumnValue = null } =
      initialHeaders.find(
        (_, index) => !!initialHeadersCellsProps[index]?.enableSort && !!initialHeadersCellsProps[index]?.initialSort
      ) || {}

    if (initialColumnValue) {
      return initialColumnValue
    }

    const { value: firstColumnValue = null } =
      initialHeaders.find((_, index) => !!initialHeadersCellsProps[index]?.enableSort) || {}

    return firstColumnValue
  })
  const [sortDirectionValue, setSortDirection] = useState(initialDirectionValue)
  const [sortHoverValue, setSortHover] = useState(null)
  const { value: searchValue, onChange: handleChangeSearch } = useInput({
    changeCallback: () => setPage(initialPage)
  })
  const columnsValue = useMemo(() => initialHeaders.length, [initialHeaders])
  const enableFilterValue = useMemo(
    () => initialHeadersCellsProps.some(cellProps => cellProps?.enableFilter),
    [initialHeadersCellsProps]
  )
  const filteredRecords = useMemo(
    () =>
      enableFilterValue
        ? filtering(initialRecords, initialHeaders, initialHeadersCellsProps, searchValue)
        : initialRecords,
    [enableFilterValue, initialHeaders, initialHeadersCellsProps, initialRecords, searchValue]
  )
  const sortedRecords = useMemo(() => {
    return sortDirectionValue === SORT_DIRECTIONS_DICTIONARY.DESC
      ? descendingSort(filteredRecords, sortColumnValue)
      : ascendingSort(filteredRecords, sortColumnValue)
  }, [filteredRecords, sortColumnValue, sortDirectionValue])
  const {
    page: pageValue,
    totalPages: pagesValue,
    onChangePage,
    onGetCurrentPage,
    setPage
  } = usePagination({
    initialPage,
    initialRowsPerPage: initialRows,
    data: sortedRecords
  })
  const handleClickSort = useSecondOrderCallback(
    (_, __, updatedColumnValue) => {
      let updatedDirectionValue =
        sortColumnValue === updatedColumnValue && sortDirectionValue === SORT_DIRECTIONS_DICTIONARY.DESC
          ? SORT_DIRECTIONS_DICTIONARY.ASC
          : SORT_DIRECTIONS_DICTIONARY.DESC

      if (sortColumnValue !== updatedColumnValue) {
        updatedDirectionValue = initialDirectionValue
      }

      setSortColumn(updatedColumnValue)
      setSortDirection(updatedDirectionValue)
    },
    [sortColumnValue, sortDirectionValue]
  )
  const handleHoverSort = useSecondOrderCallback((_, __, updatedValue) => setSortHover(updatedValue), [sortHoverValue])

  return {
    columnsValue,
    enableFilterValue,
    initialDirectionValue,
    pageValue,
    pagesValue,
    searchValue,
    sortColumnValue,
    sortDirectionValue,
    sortHoverValue,
    onChangePage,
    onGetCurrentPage,
    headersValues: initialHeaders,
    headersCellsPropsValues: initialHeadersCellsProps,
    onChangeSearch: handleChangeSearch,
    onClickSort: handleClickSort,
    onHoverSort: handleHoverSort,
    onSetPage: setPage
  }
}

export default useDataTable
